import { css } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';

import HyperlinkColorEnum from '../../enums/HyperlinkColorEnum.js';

const DEFAULT_ICON_SIZE = 24;

/**
 * Style for Hyperlink.
 */
export default class HyperlinkStyle {
	/**
	 * Get the styles.
	 *
	 * @param options Options.
	 * @param options.underline True if there should be a underline when it's in the idle state.
	 * @param options.theme Theme.
	 * @param options.color Link colors.
	 * @param options.hasIcon Icon.
	 * @returns Styles.
	 */
	public static getStyle(options: {
		underline: boolean;
		theme: ITheme;
		color: HyperlinkColorEnum;
		hasIcon: boolean;
	}): string {
		const { spacing, motion } = options.theme;
		return css`
			:host {
				display: inline;
				cursor: default;
			}

			a {
				position: relative;
				cursor: pointer;
				text-decoration: ${options.underline ? 'underline' : 'none'};
				color: ${this.getColor(options)};
				padding: 0;
				border: 0;

				outline-style: solid;
				outline-color: transparent;
				outline-width: 0;
				outline-offset: 0;
				box-shadow: 0 0 0 0 transparent;
				transition-property: box-shadow, outline-offset, outline-color;
				transition-duration: ${motion.duration.large};
				transition-timing-function: ${motion.ease.easy};
				display: inline;
			}

			kompis-icon {
				display: inline-flex;
				vertical-align: middle;
				width: ${DEFAULT_ICON_SIZE / 16}rem;
				height: ${DEFAULT_ICON_SIZE / 16}rem;
			}

			.divider {
				display: inline-flex;
				padding-left: ${spacing.space50 / 16}rem;
			}

			a:hover {
				text-decoration: underline;
				color: ${this.getHoverColor(options)};
			}

			@media (pointer: coarse) {
				a:hover {
					text-decoration: none;
				}
			}
		`;
	}

	/**
	 * Get link Color.
	 *
	 * @param options Options.
	 * @param options.color Link color.
	 * @param options.theme Theme.
	 */
	private static getColor(options: { color: HyperlinkColorEnum; theme: ITheme }): string {
		const { color } = options.theme;
		switch (options.color) {
			case HyperlinkColorEnum.grey:
				return color.text.default;
			case HyperlinkColorEnum.black:
				return color.text.dark;
			case HyperlinkColorEnum.white:
				return color.text.inverse;
			default:
				return color.text.default;
		}
	}

	/**
	 * Get link hover color.
	 *
	 * @param options Options.
	 * @param options.color Link color.
	 * @param options.theme Theme.
	 */
	private static getHoverColor(options: { color: HyperlinkColorEnum; theme: ITheme }): string {
		const { color } = options.theme;
		switch (options.color) {
			case HyperlinkColorEnum.grey:
				return color.text.dark;
			case HyperlinkColorEnum.black:
				return color.text.default;
			case HyperlinkColorEnum.white:
				return color.neutral.grey200;
			default:
				return color.text.default;
		}
	}
}
